import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useTenant } from './useTenant';

export function useLanguage() {
  const { i18n, t } = useTranslation();
  const { config } = useTenant();

  const changeLanguage = (lng: string) => {
    // Verificar si el idioma está habilitado para el tenant actual
    const enabledLanguages = config?.languages || ['es', 'en'];
    
    if (enabledLanguages.includes(lng)) {
      i18n.changeLanguage(lng);
      
      // Guardar preferencia en localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('preferredLanguage', lng);
      }
    } else {
      console.warn(`Language ${lng} is not enabled for current tenant`);
    }
  };

  const currentLanguage = i18n.language;

  // Obtener idiomas disponibles para el tenant actual
  const getAvailableLanguages = () => {
    return config?.languages || ['es', 'en'];
  };

  // Verificar si un idioma está disponible
  const isLanguageAvailable = (lng: string) => {
    return getAvailableLanguages().includes(lng);
  };

  // Obtener información del idioma actual
  const getCurrentLanguageInfo = () => {
    const languageMap = {
      es: { name: 'Español', nativeName: 'Español', flag: '🇪🇸' },
      en: { name: 'English', nativeName: 'English', flag: '🇺🇸' },
      pt: { name: 'Português', nativeName: 'Português', flag: '🇧🇷' },
      fr: { name: 'Français', nativeName: 'Français', flag: '🇫🇷' },
      de: { name: 'Deutsch', nativeName: 'Deutsch', flag: '🇩🇪' },
      it: { name: 'Italiano', nativeName: 'Italiano', flag: '🇮🇹' },
      zh: { name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
      ja: { name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' }
    };

    return languageMap[currentLanguage as keyof typeof languageMap] || languageMap.es;
  };

  useEffect(() => {
    // Aplicar dirección del texto según idioma
    document.documentElement.dir = i18n.dir();
    
    // Aplicar idioma del documento
    document.documentElement.lang = currentLanguage;
  }, [i18n.language, currentLanguage]);

  useEffect(() => {
    // Cargar idioma preferido del localStorage al inicializar
    if (typeof window !== 'undefined') {
      const savedLanguage = localStorage.getItem('preferredLanguage');
      if (savedLanguage && isLanguageAvailable(savedLanguage) && savedLanguage !== currentLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, [config?.languages]);

  return {
    t,
    currentLanguage,
    changeLanguage,
    languages: getAvailableLanguages(),
    availableLanguages: getAvailableLanguages(),
    isLanguageAvailable,
    getCurrentLanguageInfo,
    isMultiLanguageEnabled: config?.features?.multiLanguage || false
  };
}